/*
  소개글 HTML 후처리 모음.
  에디터 인스턴스가 필요 없는 순수 문자열 변환만 둔다.
  (react-quill 을 끌고 오지 않아야 읽기 전용 뷰에서도 가볍게 쓸 수 있다)
 */

const TABLE_TEMPORARY_SELECTOR = 'temporary.ql-table-temporary';

const isIgnorableNode = (node: ChildNode) => {
  if (node.nodeName === 'BR') {
    return true;
  }

  return node.nodeType === Node.TEXT_NODE && !node.textContent?.trim();
};

/*
  quill-table-better 는 표의 width/class 를 델타에 실어 나르려고 표 앞에
  <temporary class="ql-table-temporary"> 라는 빈 줄을 만들고,
  그 줄을 .ql-table-temporary { display: none } 으로 숨긴다.
  높이가 0일 뿐 Quill 문서상으로는 멀쩡한 한 줄이라 커서가 올라갈 수 있어서,
  표 바로 위에서 이미지를 넣으면 이 보이지 않는 줄에 갇혀 소개글에서 통째로 사라진다.

  갇힌 내용만 바로 앞 문단으로 꺼내고, temporary 엘리먼트와 속성은
  표 메타데이터라 그대로 둔다.
 */
export const normalizeTableTemporary = (html: string): string => {
  if (!html || !html.includes('ql-table-temporary')) {
    return html;
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const temporaries = doc.querySelectorAll(TABLE_TEMPORARY_SELECTOR);

  if (!temporaries.length) {
    return html;
  }

  let isChanged = false;

  temporaries.forEach(temporary => {
    const trapped = Array.from(temporary.childNodes).filter(
      node => !isIgnorableNode(node),
    );

    if (!trapped.length) {
      return;
    }

    const paragraph = doc.createElement('p');
    trapped.forEach(node => paragraph.appendChild(node));

    temporary.parentNode?.insertBefore(paragraph, temporary);
    temporary.innerHTML = '<br>';

    isChanged = true;
  });

  return isChanged ? doc.body.innerHTML : html;
};

const TABLE_CELL_BLOCK_EMBED_SELECTOR = 'td iframe, th iframe, td hr, th hr';

const getOutermostTable = (element: Element): Element | null => {
  let table = element.closest('table');

  while (table?.parentElement?.closest('table')) {
    table = table.parentElement.closest('table');
  }

  return table;
};

/*
  표 셀에는 영상(iframe)·구분선(hr) 같은 블록 임베드가 들어갈 수 없다.
  셀 안에 있으면 quill-table-better 가 표를 그 지점에서 쪼개는데, 쪼개진 뒤쪽 표는
  메타데이터 줄이 없어 바로 앞 블록(영상·구분선)에 descendant() 를 부르다
  "descendant is not a function" 으로 터지고 소개글이 열리지 않는다.
  운영 어드민(Froala)은 셀 안에 영상을 넣을 수 있어서, 표를 칸 나누기로 쓰며
  셀마다 영상을 넣은 소개글이 실제로 있다.

  셀 안의 영상·구분선을 원래 순서대로 가장 바깥 표 바로 뒤로 옮긴다.
  옮기고 나서 글자도 이미지도 남지 않은 표는 영상을 담는 칸으로만 쓰인 것이라 지운다.
 */
export const moveBlockEmbedsOutOfTableCells = (html: string): string => {
  if (!html || !/<(iframe|hr)\b/i.test(html) || !/<t[dh]\b/i.test(html)) {
    return html;
  }

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const embeds = Array.from(
    doc.querySelectorAll(TABLE_CELL_BLOCK_EMBED_SELECTOR),
  );

  if (!embeds.length) {
    return html;
  }

  // 표마다 마지막으로 옮긴 위치. 다음 임베드는 그 뒤에 붙여 원래 순서를 지킨다
  const lastMovedByTable = new Map<Element, Element>();

  embeds.forEach(embed => {
    const table = getOutermostTable(embed);

    if (!table) {
      return;
    }

    (lastMovedByTable.get(table) ?? table).after(embed);
    lastMovedByTable.set(table, embed);
  });

  lastMovedByTable.forEach((_, table) => {
    if (!table.textContent?.trim() && !table.querySelector('img')) {
      table.remove();
    }
  });

  return doc.body.innerHTML;
};
